import React from "react";
import { FiShield, FiZap, FiGlobe, FiCpu, FiCode, FiLayers, FiLayout, FiSearch, FiSmartphone } from "react-icons/fi";
import { FaAngular, FaTerminal, FaProjectDiagram, FaCheckDouble } from "react-icons/fa";
import { LuSettings, LuUsers, LuSmartphone, LuShieldCheck, LuPercent, LuShield, LuCircleCheck, LuZap, LuClipboardList, LuPalette, LuCode, LuBug, LuRocket } from "react-icons/lu";

// 1. Hero Block
export const AngularAppHeroData = {
  backgroundImage: "/images/serviceBanners/AngularBanner.webp",
  whiteImage: "/images/serviceBanners/AngularW.webp",
  subTitle: "Enterprise Angular Development Solutions",
  title: (
    <>
      Structured <br /> Enterprise <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Angular</span> Development
    </>
  ),
  description:
    "We build scalable and high performance Angular applications using structured frameworks designed for enterprise grade systems, ensuring long term maintainability, smooth performance, and robust digital solutions for complex business needs.",
  primaryButton: {
    title: "Get a Free Quote",
    onClick: () => console.log("Angular Quote clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("Angular Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "TypeScript Power",
      desc: "Strongly typed code for safer and more maintainable Angular applications.",
      iconColor: "#dd0031",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Two-Way Binding",
      desc: "Real-time sync between data and UI for seamless interaction.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Modular Design",
      desc: "Structured architecture built for scalable enterprise applications.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Full-Featured",
      desc: "Complete tooling for building end-to-end Angular solutions.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(221,0,49,0.2)",
      iconGlow: "rgba(221,0,49,0.8)",
    },
  ],
};

// 2. Stats Block
export const angularStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block
export const angularSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "Full-Featured Angular Architectures for Complex Applications",
  buildCards: [
    {
      title: "Enterprise Apps",
      desc: "Build scalable Angular applications for large enterprise systems with powerful patterns.",
      icon: FaAngular,
      colorClass: "text-[#dd0031]",
      bgClass: "bg-[#dd0031]/10",
      borderClass: "border-[#dd0031]/20 hover:border-[#dd0031]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(221,0,49,0.25)]",
    },
    {
      title: "Angular SSR",
      desc: "Improve SEO and speed with server-side rendering using Angular Universal.",
      icon: FiCpu,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Reactive RxJS",
      desc: "Handle real-time data and complex interactions smoothly using asynchronous streams.",
      icon: FiLayers,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "PWA Apps",
      desc: "Create fast, installable Angular web applications with offline capabilities.",
      icon: FiGlobe,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Modular Architecture",
      desc: "Structured and reusable codebases designed for absolute scalability and maintenance.",
      icon: FiShield,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Performance Optimization",
      desc: "Improve speed, efficiency, and user experience through profiling and deep tuning.",
      icon: FiZap,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR DEVELOPMENT PROCESS",
  processTitle: "Structured Angular Delivery Roadmap",
  processSteps: [
    {
      step: "01",
      title: "Module Architecture",
      description: "Design scalable Angular structure using dependency injection for maintainability.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Feature Build",
      description: "Develop features using TypeScript and Angular’s declarative templates.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Stability Testing",
      description: "Ensure bug-free performance using Jasmine and Karma testing suites.",
      icon: LuBug,
    },
    {
      step: "04",
      title: "Performance Optimization",
      description: "Improve load speed and application efficiency for better enterprise UX.",
      icon: LuZap,
    },
    {
      step: "05",
      title: "Code Quality Review",
      description: "Maintain clean, scalable, and production-ready Angular code via peer reviews.",
      icon: LuCircleCheck,
    },
    {
      step: "06",
      title: "Deployment & Delivery",
      description: "Deploy stable Angular applications with structured release pipelines.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE ANGULAR?",
  benefitsTitle: "Enterprise-Grade Angular Reliability",
  benefits: [
    "Strict TypeScript typing for safety",
    "Clean dependency injection patterns",
    "Modular architecture design",
    "CLI-based structured development",
    "NgRx state management system",
    "Material Design components",
    "Scalable enterprise codebase",
  ],
  logoImage: "/images/serviceBanners/AngularBanner.webp",
  whiteLogoImage: "/images/serviceBanners/AngularW.webp",
};

// 4. Portfolio Block
export const angularPortfolioData = {
  subtitle: "OUR CASE STUDIES",
  title: "Angular Systems Powering Industry Leaders",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "Enterprise CRM",
      badge: "SaaS Platform",
      badgeColor: "bg-[#dd0031]/10 text-[#dd0031] border-[#dd0031]/20",
      desc: "A sophisticated customer relationship portal with real-time data binding and RxJS stream management.",
      downloads: "Enterprise Ready",
      rating: "5.0",
      image: "/images/fullstack-dev.jpg",
    },
    {
      title: "Global FinPortal",
      badge: "Universal App (SSR)",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "High-traffic financial dashboard utilizing Angular Universal for instant server-side loading.",
      downloads: "Fast SEO",
      rating: "4.9",
      image: "/images/webapp-dev.jpg",
    },
  ],
};

// 5. Technologies Block
export const angularTechnologiesData = {
  techTitle: "OUR EXTENSIVE TOOL STACK",
  technologies: [
    { name: "Angular", icon: "Angular" },
    { name: "TypeScript", icon: "TypeScript" },
    { name: "RxJS", icon: "RxJS" },
    { name: "Angular CLI", icon: "CLI" },
    { name: "NgRx", icon: "NgRx" },
    { name: "Material Design", icon: "Material" },
  ],
  trustTitle: "TRUSTED BY CORPORATES WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

// 6. About Block
export const angularAboutData = [
  {
    title: "Angular Development Services for Scalable Enterprise Applications",
    content: (
      <>
        Modern businesses require web applications that remain stable, scalable, and easy to manage as operational needs grow. Reliable{" "}
        <b className="text-text-primary font-semibold">angular development services</b> help organizations create structured digital products that support high performance and
        long-term flexibility. Many businesses face challenges with systems that become difficult to maintain as features expand over time. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, Angular applications are built around usability, structured architecture, and business objectives to
        help companies create reliable enterprise-grade digital experiences.
      </>
    ),
  },
  {
    title: "Angular JS Development Company for High-Performance Web Systems",
    content: (
      <>
        Creating enterprise applications requires more than strong visuals and modern layouts. A trusted{" "}
        <b className="text-text-primary font-semibold">angular js development company</b> helps businesses develop stable platforms that improve efficiency, maintain organized
        workflows, and support complex business operations. Companies investing in <b className="text-text-primary font-semibold">angular web development services</b> often
        experience stronger application reliability because frameworks are designed for structured growth and long-term maintainability. Tycho Technologies focuses on practical
        Angular ecosystems designed to improve performance while supporting future business scalability.
      </>
    ),
  },
  {
    title: "Angular App Development Company for Better User Experiences",
    content: (
      <>
        Digital users expect responsive applications that perform smoothly and simplify interactions. A reliable{" "}
        <b className="text-text-primary font-semibold">angular app development company</b> helps organizations build intuitive platforms that improve engagement and support better
        operational performance. Businesses looking to strengthen frontend capabilities often combine modern frameworks with{" "}
        <a href="http://tychotechnologies.com/vuejs-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          vue js development services
        </a>{" "}
        to improve flexibility and create connected user experiences across digital ecosystems.
      </>
    ),
  },
  {
    title: "Why Businesses Choose an Angular Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">angular development company in india</b> continues to grow as organizations seek scalable and
        future-ready application frameworks. Companies looking to <b className="text-text-primary font-semibold">hire angular developers</b> often prioritize long-term
        maintainability, strong application structure, and better business performance. Businesses partnering with Tycho Technologies gain access to customized Angular strategies
        designed around measurable business goals and operational needs. Many organizations also improve frontend adaptability by working with a trusted{" "}
        <a href="https://tychotechnologies.com/reactjs-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          react js development company in india
        </a>{" "}
        to strengthen modern digital product experiences.
      </>
    ),
  },
];
// 7. FAQs
export const angularFaqs = [
  {
    q: "Why should I choose Angular JS development in India for my project?",
    a: "Angular development in India offers cost-effective solutions with skilled developers who can build fast, scalable, and dynamic web applications following clean code standards.",
  },
  {
    q: "Why hire an Angular website development company in Delhi/NCR?",
    a: "Delhi/NCR has experienced Angular developers who deliver reliable, high-quality web solutions with smooth communication and secure architectures.",
  },
];

// 8. Legacy compatibility
export const angularTechStack = ["Angular", "TypeScript", "RxJS", "Angular CLI", "NgRx", "Jasmine/Karma", "Material Design"];
